export const supportedLanguages = [
  {
    locale: 'cs-CZ',
    dateFnsLocale: 'cs',
    name: 'Czech',
  },
  {
    locale: 'da-DK',
    dateFnsLocale: 'da',
    name: 'Danish',
  },
  {
    locale: 'de-DE',
    dateFnsLocale: 'de',
    name: 'German',
  },
  {
    locale: 'el-GR',
    dateFnsLocale: 'el',
    name: 'Greek',
  },
  {
    locale: 'en-US',
    dateFnsLocale: 'en-US',
    name: 'English (United States)',
  },
  {
    locale: 'es-419',
    dateFnsLocale: 'es',
    name: 'Spanish',
  },
  {
    locale: 'es-ES',
    dateFnsLocale: 'es',
    name: 'Spanish (Spain)',
  },
  {
    locale: 'et-EE',
    dateFnsLocale: 'et',
    name: 'Estonian',
  },
  {
    locale: 'fi-FI',
    dateFnsLocale: 'fi',
    name: 'Finnish',
  },
  {
    locale: 'fr-FR',
    dateFnsLocale: 'fr',
    name: 'French',
  },
  {
    locale: 'he-IL',
    dateFnsLocale: 'he',
    name: 'Hebrew',
  },
  {
    locale: 'hu-HU',
    dateFnsLocale: 'hu',
    name: 'Hungarian',
  },
  {
    locale: 'id-ID',
    dateFnsLocale: 'id',
    name: 'Indonesian',
  },
  {
    locale: 'it-IT',
    dateFnsLocale: 'it',
    name: 'Italian',
  },
  {
    locale: 'ja-JP',
    dateFnsLocale: 'ja',
    name: 'Japanese',
  },
  {
    locale: 'ko-KR',
    dateFnsLocale: 'ko',
    name: 'Korean',
  },
  {
    locale: 'lv-LV',
    dateFnsLocale: 'lv',
    name: 'Latvian',
  },
  {
    locale: 'lt-LT',
    dateFnsLocale: 'lt',
    name: 'Lithuanian',
  },
  {
    locale: 'ms-MY',
    dateFnsLocale: 'ms',
    name: 'Malay',
  },
  {
    locale: 'nl-NL',
    dateFnsLocale: 'nl',
    name: 'Dutch',
  },
  {
    locale: 'no-NO',
    dateFnsLocale: 'nb',
    name: 'Norwegian',
  },
  {
    locale: 'pl-PL',
    dateFnsLocale: 'pl',
    name: 'Polish',
  },
  {
    locale: 'pt-BR',
    dateFnsLocale: 'pt-BR',
    name: 'Portuguese (Brazil)',
  },
  {
    locale: 'pt-PT',
    dateFnsLocale: 'pt',
    name: 'Portuguese (Portugal)',
  },
  {
    locale: 'ro-RO',
    dateFnsLocale: 'ro',
    name: 'Romanian',
  },
  {
    locale: 'ru-RU',
    dateFnsLocale: 'ru',
    name: 'Russian',
  },
  {
    locale: 'sk-SK',
    dateFnsLocale: 'sk',
    name: 'Slovak',
  },
  {
    locale: 'sv-SE',
    dateFnsLocale: 'sv',
    name: 'Swedish',
  },
  {
    locale: 'th-TH',
    dateFnsLocale: 'th',
    name: 'Thai',
  },
  {
    locale: 'tr-TR',
    dateFnsLocale: 'tr',
    name: 'Turkish',
  },
  {
    locale: 'uk-UA',
    dateFnsLocale: 'uk',
    name: 'Ukrainian',
  },
  {
    locale: 'vi-VN',
    dateFnsLocale: 'vi',
    name: 'Vietnamese',
  },
  {
    locale: 'zh-Hant',
    dateFnsLocale: 'zh-TW',
    name: 'Chinese (Traditional)',
  },
] as const;

export const LOCALES = supportedLanguages.map((language) => language.locale);
export const DEFAULT_LOCALE = 'en-US';

export type Locale = (typeof supportedLanguages)[number]['locale'];

export const loadMessages = async (locale: Locale) => {
  try {
    const messagesModule = await import(`../../lang-compiled/${locale}.json`);
    return messagesModule.default;
  } catch (error) {
    console.error('Failed to load translations for locale', locale);
  }
};

/**
 * Map an app locale tag to a language code accepted by the MFD smart-policies
 * endpoint, whose validation is /^[a-z]{2}([_-][A-Za-z]{2})?$/i (two-letter
 * language, optional two-LETTER region). Tags with a non-two-letter subtag
 * (e.g. es-419, zh-Hant) would 400, so fall back to the primary subtag.
 * Note: zh-Hant intentionally degrades to the safe, valid 'zh' (script subtags
 * are not expressible here); revisit only if MFD's policy catalog is confirmed
 * to hold a distinct region-keyed Traditional-Chinese entry.
 */
export const toApiLanguageCode = (locale: string): string => {
  const [primary, region] = locale.split(/[-_]/);
  return region && /^[A-Za-z]{2}$/.test(region)
    ? `${primary}-${region}`
    : primary;
};

export const loadDateFnsLocale = async (locale: Locale) => {
  try {
    const dateFnsLocale =
      supportedLanguages.find((language) => language.locale === locale)
        ?.dateFnsLocale ?? DEFAULT_LOCALE;
    const localeModule = await import(
      `date-fns/locale/${dateFnsLocale}/index.js`
    );
    return localeModule.default;
  } catch (error) {
    console.error('Failed to load date-fns locale for locale', locale);
  }
};
